<script setup lang="ts">
import { computed } from 'vue';
import InsightActionCard from '@/components/home/insights/insight-action-card.vue';
import InsightHintReasonTag from '@/components/home/insights/insight-hint-reason-tag.vue';
import { arrangeInsightActions } from '@/components/insights/insight-action-display';
import InsightTagTitle from '@/components/insights/insight-tag-title.vue';
import type { InsightHint } from '@/types/insights';

interface Props {
  hint: InsightHint;
  plain?: boolean;
  actionsDisabled?: boolean;
  actionsDisabledReason?: string;
}
const props = defineProps<Props>();

const actionCells = computed(() =>
  arrangeInsightActions(props.hint.recommended_actions),
);
</script>

<template>
  <div class="insight-hint-card" :class="{ plain }">
    <div v-if="$slots.header" class="header">
      <slot name="header" />
    </div>

    <div class="body">
      <div class="section">
        <InsightTagTitle variant="focus-point" />
        <div class="c-text-regular--m">
          {{ hint.focus_point }}
          <InsightHintReasonTag
            label="着眼点の根拠"
            :reasons="[{ text: hint.focus_point_reason }]"
          />
        </div>
      </div>

      <div class="section">
        <InsightTagTitle variant="hypothesis" />
        <div class="c-text-regular--m">
          {{ hint.hypothesis }}
          <InsightHintReasonTag
            label="仮説の根拠"
            :hint-id="hint.id"
            :reasons="hint.hypothesis_reasons"
          />
        </div>
      </div>

      <div class="section">
        <InsightTagTitle variant="recommendation" />
        <div class="actions">
          <InsightActionCard
            v-for="cell in actionCells"
            :key="cell.action.uuid"
            :class="`${cell.column}-action`"
            :style="{ gridRow: cell.row }"
            :action="cell.action"
            :hint-id="hint.id"
            :disabled="actionsDisabled"
            :disabled-reason="actionsDisabledReason"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.insight-hint-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background-color: $color-gray200;
  border-radius: 4px;

  &.plain {
    padding: 0;
    background-color: transparent;
    border-radius: 0;

    // 白背景の上では白い推奨アクションの輪郭が消えるため、枠線で区切る。
    .actions :deep(.insight-action-card) {
      border: 1px solid $color-gray400;
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid $color-gray400;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  // 片方の列が空でも、もう片方が自動配置で左列に詰められないよう列位置を固定する。
  .agent-action {
    grid-column: 1;
  }
  .survey-action {
    grid-column: 2;
  }
}
</style>
